"use client";

import {
  type ClassAttributes,
  type FC,
  type OlHTMLAttributes,
  useRef,
  useState,
  memo,
  type HTMLAttributes,
} from "react";
import MarkdownParser, { type ExtraProps } from "react-markdown";
import { mergeClasses } from "@huub-web/utils";

import { Card } from "../Card";
import {
  cardStyle,
  imageContainerStyle,
  imageStyle,
  markdownListStyle,
} from "./Markdown.css";
import { polymorphicForwardRef } from "../../props/PolymorphicProps";
import { useIsomorphicLayoutEffect } from "../../hooks/useIsomorphicLayoutEffect";
import { mergeRefs } from "../../utils/mergeRefs";
import type { ResponseVariant } from "../../types";
import { useMaybePubSub } from "../../hooks/usePubSub";

export type MarkdownProps = {
  content: string;
  variant?: ResponseVariant;
};

/**
 * A markdown component that renders markdown content.
 */
export const Markdown: FC<MarkdownProps> = memo(({ variant, content }) => {
  const pubSub = useMaybePubSub();

  return (
    <MarkdownParser
      components={{
        ol: (props) => <List as="ol" variant={variant} {...props} />,
        ul: (props) => <List as="ul" variant={variant} {...props} />,
        h1: (props) => <Heading as="h1" variant={variant} {...props} />,
        h2: (props) => <Heading as="h2" variant={variant} {...props} />,
        h3: (props) => <Heading as="h3" variant={variant} {...props} />,
        h4: (props) => <Heading as="h4" variant={variant} {...props} />,
        h5: (props) => <Heading as="h5" variant={variant} {...props} />,
        hr: () => null,
        img: (props) => (
          <div className={imageContainerStyle}>
            <img alt="" {...props} className={imageStyle} />
          </div>
        ),
        a: ({ href, ...props }) => {
          if (href?.startsWith("/")) {
            return (
              <a
                {...props}
                href={href}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  pubSub?.publish("navigateToPath", { path: href });
                }}
              />
            );
          }
          return (
            <a
              {...props}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
            />
          );
        },
      }}
    >
      {content}
    </MarkdownParser>
  );
});

const List = polymorphicForwardRef<
  "li",
  ClassAttributes<HTMLElement> &
    OlHTMLAttributes<HTMLElement> &
    ExtraProps & { variant?: ResponseVariant }
>(
  (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    { as: Element = "li", className, children, variant, node, ...props },
    forwardedRef,
  ) => {
    const ref = useRef<HTMLElement>(null);
    const isInitialised = useRef(false);
    const [isTopLevelList, setIsTopLevelList] = useState(false);

    useIsomorphicLayoutEffect(() => {
      if (variant !== "hub") {
        return;
      }

      if (
        ref.current?.parentNode &&
        !isTopLevelList &&
        !isInitialised.current
      ) {
        isInitialised.current = true;
        setIsTopLevelList(
          (ref.current.parentNode as Element).classList.contains(
            "--chat-mode",
          ) ?? false,
        );
      }
    }, [ref]);

    return isTopLevelList && variant === "hub" ? (
      <Card border="default" className={mergeClasses(className, cardStyle)}>
        <Element ref={mergeRefs(forwardedRef, ref)} {...props}>
          {children}
        </Element>
      </Card>
    ) : (
      <Element
        ref={mergeRefs(forwardedRef, ref)}
        className={mergeClasses(
          markdownListStyle({
            kind: Element === "ol" ? "ordered" : "unordered",
          }),
          className,
        )}
        {...props}
      >
        {children}
      </Element>
    );
  },
);

const Heading = polymorphicForwardRef<
  "h1",
  ClassAttributes<HTMLElement> &
    HTMLAttributes<HTMLHeadingElement> &
    ExtraProps & { variant?: ResponseVariant }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(({ as: Element = "li", children, variant, node, ...props }, forwardedRef) =>
  variant === "hub" ? (
    <Element ref={forwardedRef} {...props}>
      {children}
    </Element>
  ) : (
    // TODO: Symantics
    <p>
      <b>{children}</b>
    </p>
  ),
);
